import _ from 'lodash';

import {
	action,
	computed,
	observable,
	runInAction
} from 'mobx';

import apiStore from './api-store';

const commonOptions = {
	contentType: 'application/json',
	accept: 'application/json'
};

const DFLT_PAGE_LIMIT = 12;

export default class BaseStore {
	/**
	 * Observables & Computeds
	 */

	@observable
	_data = {
		item: observable.map({})
	};

	_itemsIsReallyEmpty = false;

	@observable
	_items = [];

	@computed
	get items () {
		return this._items;
	}

	set items (items) {
		this._items = items;
	}

	@observable
	_pagination = {};

	@computed
	get pagination () {
		return this._pagination;
	}

	set pagination (pagination) {
		this._pagination = pagination;
	}

	@observable
	_isLoading = false;

	@computed
	get isLoading () {
		return this._isLoading;
	}

	set isLoading (loading) {
		this._isLoading = loading;
	}

	@action
	setIsLoading (isLoading) {
		this._isLoading = isLoading;
	}

	@observable
	_arePagesLoading = false;

	@computed
	get arePagesLoading () {
		return this._arePagesLoading;
	}

	set arePagesLoading (loading) {
		this._arePagesLoading = loading;
	}

	/**
	 * Actions
	 */

	@action
	clearDataItem () {
		this._data.item.clear();
	}

	@action.bound
	async fetch (opts = {}) {
		const { id = null, route = null, returnData = false, silent = false } = opts;
		const apiRoute = _.isEmpty(route) ? _.result(this, 'apiRoute', null) : route;

		if (_.isNull(apiRoute)) {

			console.warn('[BaseStore.fetch] No apiRoute set');
			return;
		}

		this.isLoading = silent !== true;

		try {

			const data = await apiStore.fetch({
				route: _.isString(id) ? `${apiRoute}/${id}` : apiRoute
			});

			this._itemsIsReallyEmpty = _.isEmpty(data);

			if (returnData) {

				return data;
			}

			this.onFetchSuccess(data);
		} catch (err) {

			console.error('[ERROR: BaseStore.fetch]', err);
		} finally {

			this._isLoading = false;
		}
	}

	@action.bound
	async fetchIfEmpty (opts = {}) {

		if (_.isEmpty(this._items) && this._itemsIsReallyEmpty !== true) {

			await this.fetch(opts);
		}
	}

	@action
	onFetchSuccess (data) {
		this._items = data;
	}

	@action
	async fetchPagedItems (page = 1, limit = DFLT_PAGE_LIMIT) {
		const fetchPagedItemsRoute = _.result(this, 'fetchPagedItemsRoute', null);

		if (_.isNull(fetchPagedItemsRoute)) {

			console.warn('[BaseStore.fetchPaged] No fetchPagedItemsRoute set');
			return;
		}

		this.arePagesLoading = true;

		try {

			const route = fetchPagedItemsRoute
				.replace(/\:page/, page)
				.replace(/\:limit/, limit);

			const data = await this.fetch({ route });
			this._pagination = observable(data);
		} catch (err) {

			console.error('[ERROR: BaseStore.fetchPagedItems]', err);
		} finally {

			this.arePagesLoading = false;
		}
	}

	@action
	async save (item, opts = {}) {
		const route = opts.route || _.result(this, 'apiRoute', null);

		if (_.isNull(route)) {

			console.warn('[BaseStore.fetch] No route set');
			return;
		}

		const { silent = false } = opts;

		if (silent !== true) {

			this.isLoading = true;
		}

		try {
			const payload = _.isObject(item.serialized) ? item.serialized : item;
			const data = await apiStore.save({
				route, payload, ...opts
			});
			
			if (_.isFunction(item.update)) {

				item.update(data);
			}

			return data;
		} catch (err) {

			console.error('[ERROR: BaseStore.save]', err);
		} finally {

			if (opts.silent !== true) {

				this.isLoading = false;
			}
		}
	}

	@action
	async patch (item, opts = {}) {
		const route = opts.route || _.result(this, 'apiRoute', null);

		if (_.isNull(route)) {

			console.warn('[BaseStore.fetch] No route set');
			return;
		}

		const { silent = false } = opts;

		if (silent !== true) {

			this.isLoading = true;
		}

		try {
			const payload = _.isObject(item.serialized) ? item.serialized : item;
			const data = await apiStore.patch({
				route, payload, ...opts
			});
			
			if (_.isFunction(item.update)) {

				item.update(data);
			}

			return data;
		} catch (err) {

			console.error('[ERROR: BaseStore.save]', err);
		} finally {

			if (opts.silent !== true) {

				this.isLoading = false;
			}
		}
	}

	@action
	set ({ key, value, _id }) {
		const item = this.getItemByID({ _id });

		if (_.isEmpty(item)) {

			return;
		}

		item[key] = value;
	}

	@action
	async delete (item, { route = '' } = {}) {
		const apiRoute = route || _.result(this, 'apiRoute', null);

		if (_.isNull(apiRoute)) {

			console.warn('[BaseStore.fetch] No apiRoute set');
			return;
		}

		try {

			const route = item ? `${apiRoute}/${item.id}` : apiRoute;
			const response = await apiStore.delete({ route });
			if (item) {
				this._items = this._items
					.filter(({ id }) => id !== item.id);
			}
			return response;
		} catch (err) {

			console.error('[ERROR: BaseStore.delete]', err);
		}
	}

	getItemIndex (item) {
		if (_.isEmpty(this._items)) {

			return -1;
		}

		return this._items.findIndex(({ id }) => id === item.id);
	}

	getItemByID (item = {}) {
		const findID = _.isString(item) ? item : item._id;
		return this._items.find(({ _id }) => _id === findID);
	}

	/**
	 * Abstract Methods
	 */

	onDataChange () {}
}
